import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../Firebase/firebase';
import { doc, deleteDoc } from 'firebase/firestore';
import { useActiveComponent } from '../ActiveComponentContext.js';
import { useAuth } from '../../contexts/AuthContext/index.jsx';
import { generatePath } from '../../utils/categoryUtils';
import { useMediaQuery, useTheme, Box } from '@mui/material';
import Card from '@mui/material/Card';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';

const DashboardCards = ({
  id,
  // postData,
  postID,
  images = [], //array of images
  title,
  summary,
  photoURL,
  username,
  name,
  email,
  date,
  category,
  country,
  onClick,
  onEditClick,
  vertical = '200px',
  horizontal = '450px',
  deletable,
  onDelete,
}) => {
  //Context for bookmark management and Alert/login dialogs
  const {
    currentUser,
    setShowAlertDialog,
    setIsBookmarkForAlert,
    bookmarks, // Provide the bookmarks state
    toggleBookmark, // Provide the toggleBookmark function
  } = useAuth();
  const { setActivePath } = useActiveComponent();
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md')); // breakpoints for where screen size is decided
  const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));
  const [currentIndex, setCurrentIndex] = useState(0); // Index for the current image
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const isBookmarked = bookmarks[postID] || false;
  const navigate = useNavigate();

  const showPostInfo = () => {
    onClick();
    handleClose();
  };
  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNextImage = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
  };

  const handlePrevImage = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  };

  const handleCategoryClick = (category) => {
    const path = generatePath(category);
    setActivePath(path); // Update the active path
    navigate(path);
  };

  //Handle navigation to username page, when it's clicked
  const handleUsernameClick = (path) => {
    navigate(path);
    setActivePath(path);
  };

  //For use in Created post for user to delete their own post
  const handleDeletePost = async () => {
    const confirmDelete = window.confirm(
      'Are you sure you want to delete this post?'
    );
    if (!confirmDelete) return;
    try {
      const postDocRef = doc(db, 'posts', postID);
      await deleteDoc(postDocRef);
      alert('Post deleted successfully');
      // Call the onDelete function passed as a prop to update the state in CreatedPosts
      onDelete();
    } catch (error) {
      console.error('Error deleting post:', error);
      alert('Failed to delete post. Please try again later.');
    }
  };

  //used to Handle Bookmark, with isBookmarked stored in context:
  const handleBookmarkClick = () => {
    if (!currentUser) {
      setShowAlertDialog(true);
      setIsBookmarkForAlert(true);
      return;
    }

    // different Info sent to firebase when post is bookmared
    toggleBookmark(postID, {
      creatorsUserId: id || null,
      title,
      summary,
      images,
      photoURL,
      username,
      //name,
      email,
      date,
      category,
      country: country || null,
      //city: city || null
    });
  };

  const height = isMdUp && isLgUp ? vertical : '260px';
  const cardWidth = isMdUp && isLgUp ? horizontal : '480px';

  return (
    <Card
      sx={{
        p: 1.2,
        maxWidth: 400,
        borderRadius: 4,
        ml: 'auto',
        mr: 'auto',
        width: { xs: 300, sm: 300, md: 350, lg: cardWidth, xl: cardWidth },
        height: {xs:270,sm:270,md:260,lg:245,xl:245},
        minHeight: { lg: 200, xl: 200 },
        '&:hover': {
          cursor: 'pointer',
          boxShadow: '0px 4px 20px rgba(0, 0, 50, 0.2)', // Custom hover effect
          transform: 'scale(1.02)', // Optional: adds a slight zoom effect
          transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
          backgroundColor: 'ghostwhite',
        },
      }}
    >
      {images && images.length > 0 ? (
        <Box position='relative'>
          <CardMedia
            component='img'
            image={images[currentIndex]}
            alt='post image'
            sx={{
              objectFit: 'cover', // Ensures the image covers the area
              height: {xs:190,sm:190,md:170,lg:160, xl:160}, // Matches the height set earlier
              width: {
                xs: 300,
                sm: 300,
                md: 350,
                lg: cardWidth,
                xl: cardWidth,
              },
              borderRadius: 4,
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              width: '95%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 0.5,
            }}
          >
            <Typography
              variant='body'
              color='black'
              sx={{
                backgroundColor: 'white',
                pl: 1,
                pr: 1,
                pt: 0.2,
                pb: 0.2,
                fontSize: { xs: 8, sm: 8, md: 8, lg: 9, xl: 9 },
                borderRadius: 3,
                ml: 1,
                cursor: 'pointer',
                fontFamily: 'Montserrat',
              }}
              onClick={() => handleCategoryClick(category)} // Handle category click
            >
              {category}
            </Typography>

            <IconButton disableRipple onClick={handleMenuClick}>
              <MoreHorizIcon
                sx={{
                  color: 'black',
                  backgroundColor: 'white',
                  p: 0.3,
                  fontSize: 11,
                  borderRadius: '50%',
                  mr: { xs: 0, sm: 0, md: 0.1, lg: 0.2, xl: 0.2 },
                }}
              />
            </IconButton>
            <Menu
              sx={{ borderRadius: 10 }}
              id='basic-menu'
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              MenuListProps={{
                'aria-labelledby': 'basic-button',
              }}
            >
              <MenuItem
                sx={{ fontSize: 10, fontFamily: 'Montserrat' }}
                onClick={showPostInfo}
              >
                Detailed View
              </MenuItem>
              {deletable && 
              <MenuItem
                sx={{ fontSize: 10, fontFamily: 'Montserrat' }}
                onClick={() => {handleClose(), onEditClick()}} // Pass card data
                //onClick={editPostInfo}
              >
                Edit Post
              </MenuItem>
              }
            </Menu>
          </Box>
          {/* Arrows only show when there is more than one image */}
          {images.length > 1 && (
            <Box>
              <IconButton
                sx={{
                  position: 'absolute',
                  width: '10px',
                  height: '10px',
                  top: '50%',
                  left: '10px',
                  transform: 'translateY(-50%)',
                  color: '#030202',
                  backgroundColor: 'white',
                  '&:hover': {
                    color: 'white',
                    backgroundColor: 'dimgrey',
                    boxShadow: '0px 2px 3px grey',
                  },
                }}
                onClick={handlePrevImage}
              >
                <ArrowBackIosIcon sx={{ fontSize: 7 }} />
              </IconButton>
              <IconButton
                sx={{
                  position: 'absolute',
                  width: '10px',
                  height: '10px',
                  top: '50%',
                  right: '10px',
                  transform: 'translateY(-50%)',
                  color: '#030202',
                  backgroundColor: 'white',
                  '&:hover': {
                    color: 'white',
                    backgroundColor: 'dimgrey',
                    boxShadow: '0px 2px 3px grey',
                  },
                }}
                onClick={handleNextImage}
              >
                <ArrowForwardIosIcon sx={{ fontSize: 7 }} />
              </IconButton>
            </Box>
          )}
        </Box>
      ): (
        <Box position='relative'>
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{
              display: 'flex',
              textAlign: 'flex-start',
              // width: { lg: '600px', xl: '600px' },
              //height of box when its just text displaying summary and no image
              height: {
                xs: '125px',
                sm: '125px',
                md: '125px',
                lg: '128px',
                xl: '128px',
              },

              overflowY: 'auto',
              borderRadius: 4,
              fontFamily: 'Montserrat',
              backgroundColor: 'black',
              color: 'white',
              m: 0,
              ml: 0,
              mr: 0,
              mt: { xs: 0.5, sm: 0.5, md: 0.5, lg: 0, xl: 0 },
              mb: 0,
              p: 2,
              fontSize: { xs: 10, sm: 10, md: 10, lg: 10, xl: 10 },
              whiteSpace: 'pre-line',
              '&:hover': {
                boxShadow: '0px 1px 2px inset grey',
                cursor: 'grab',
              },
              '&::-webkit-scrollbar': {
                width: '8px',
                backgroundColor: 'transparent',
              },
              '&:hover::-webkit-scrollbar': {
                backgroundColor: '#e0e0e0',
              },
              '&:hover::-webkit-scrollbar-thumb': {
                backgroundColor: '#888',
                borderRadius: '10px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                backgroundColor: '#555',
              },
            }}
          >
            {summary}
          </Typography>
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              //right: 0,
              p: 0.5,
              width: '95%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              variant='body'
              color='black'
              sx={{
                backgroundColor: 'white',
                pl: 1,
                pr: 1,
                pt: 0.3,
                pb: 0.3,
                fontSize: { xs: 8, sm: 8, md: 8, lg: 9, xl: 9 },
                borderRadius: 3,
                ml: 1,
                cursor: 'pointer',
                // fontFamily: 'Roboto',
                fontFamily: 'Montserrat',
                //mb: 0.5,
              }}
              onClick={() => handleCategoryClick(category)} // Handle category click
            >
              {category}
            </Typography>
            <IconButton disableRipple onClick={handleMenuClick}>
              <MoreHorizIcon
                sx={{
                  color: 'black',
                  backgroundColor: 'white',
                  p: 0.3,
                  fontSize: 11,
                  borderRadius: '50%',
                  mr: { xs: 0, sm: 0, md: 0.1, lg: 0.2, xl: 0.2 },
                }}
              />
            </IconButton>
            <Menu
              sx={{ borderRadius: 10 }}
              id='basic-menu'
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              MenuListProps={{
                'aria-labelledby': 'basic-button',
              }}
            >
              <MenuItem sx={{ fontSize: 12 }} onClick={showPostInfo}>
                Detailed View
              </MenuItem>
              {deletable && 
              <MenuItem
                sx={{ fontSize: 10, fontFamily: 'Montserrat' }}
                //onClick={editPostInfo}
              >
                Edit Post
              </MenuItem>
              }
            </Menu>
          </Box>
        </Box>
      )}
      <CardContent sx={{ m: 0, pt: 1, pl: 0.5, pr: 0.5 }}>
        <Typography
          gutterBottom
          variant='h6'
          component='div'
          sx={{
            display: 'flex',
            textAlign: 'flex-start',
            height: { xs: 40, sm: 40, md: 40, lg: 40, xl: 40 },
            maxHeight: { xs: 40, sm: 40, md: 40, lg: 40, xl: 40 },
            overflowY: 'auto',
            fontFamily: 'Montserrat',
            fontSize: { xs: 10, sm: 10, md: 11, lg: 11, xl: 11 },
            cursor: 'pointer',

            '&::-webkit-scrollbar': {
              width: '8px', // Width of the scrollbar
              backgroundColor: 'transparent', // Hide scrollbar background by default
            },
            '&:hover::-webkit-scrollbar': {
              backgroundColor: '#e0e0e0', // Show the scrollbar on hover
            },
            '&:hover::-webkit-scrollbar-thumb': {
              backgroundColor: '#888', // Show the scrollbar on hover
              borderRadius: '10px', // Round the corners of the scrollbar thumb
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: '#555', // Darker color when hovered over the scrollbar thumb
            },
          }}
        >
          {title}
        </Typography>

        {/* Bookmark Icon */}
        {deletable ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mt: -0.5,
              mb: 0,
              ml: 0,
              mr: 0,
              //border:'2px solid green'
            }}
          >
            <Box>
              <Typography
                onClick={() => handleUsernameClick(`/${username}`)}
                variant='body1'
                color='text.secondary'
                sx={{
                  mt: 0,
                  fontFamily: 'Montserrat',
                  fontSize: { xs: 8, sm: 9, md: 9, lg: 11, xl: 11 },
                  '&:hover': {
                    cursor: 'pointer',
                    textShadow: '0px 0px 0.5px black',
                  },
                }}
              >
                {photoURL && (
                  <IconButton
                    disableRipple
                    sx={{
                      p: 0,
                    }}
                  >
                    <Avatar
                      alt={name || 'User'}
                      src={photoURL || null}
                      sx={{
                        width: { xs: 22, sm: 22, md: 22, lg: 24, xl: 24 },
                        height: { xs: 22, sm: 22, md: 22, lg: 24, xl: 24 },
                        mr: 0.5,
                        ml: 0.5,
                        mb: 0,
                        fontSize: { xs: 9, sm: 9, md: 10, lg: 11, xl: 11 },
                        bgcolor: photoURL ? 'crimson' : 'violet',
                        '&:hover': {
                          cursor: 'pointer',
                          boxShadow: '0px 0px 4px black',
                        },
                      }}
                    >
                      {!photoURL && (name?.[0] || email?.[0] || 'U')}
                    </Avatar>
                  </IconButton>
                )}
                {username}
              </Typography>
            </Box>
            <Tooltip title={'Delete Post'}>
              <IconButton
                disableRipple
                onClick={handleDeletePost} // Attach the delete function here
              >
                <DeleteIcon
                  sx={{
                    fontSize: { xs: 18, sm: 18, md: 19, lg: 22, xl: 22 },
                    mt: 0,
                    color: 'indianred',
                    '&:hover': {
                      color: 'crimson',
                    },
                  }}
                />
              </IconButton>
            </Tooltip>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mt: 0,
              mb: 0,
              ml: 0,
              mr: 0,
            }}
          >
            <Box>
              <Typography
                onClick={() => handleUsernameClick(`/${username}`)}
                variant='body1'
                color='text.secondary'
                sx={{
                  mt: 0,
                  fontSize: { xs: 8, sm: 9, md: 9, lg: 11, xl: 11 },
                  '&:hover': {
                    cursor: 'pointer',
                    textShadow: '0px 0px 0.5px black',
                  },
                }}
              >
                {photoURL && (
                  <IconButton
                    disableRipple
                    sx={{
                      p: 0,
                    }}
                  >
                    <Avatar
                      alt={name || 'User'}
                      src={photoURL || null}
                      sx={{
                        width: { xs: 22, sm: 22, md: 22, lg: 24, xl: 24 },
                        height: { xs: 22, sm: 22, md: 22, lg: 24, xl: 24 },
                        mr: 0.5,
                        ml: 0.5,
                        mb: 0,
                        fontSize: { xs: 9, sm: 9, md: 10, lg: 11, xl: 11 },
                        bgcolor: photoURL ? 'crimson' : 'violet',
                        '&:hover': {
                          cursor: 'pointer',
                          boxShadow: '0px 0px 4px black',
                        },
                      }}
                    >
                      {!photoURL && (name?.[0] || email?.[0] || 'U')}
                    </Avatar>
                  </IconButton>
                )}
                {username}
              </Typography>
            </Box>
            <Tooltip title={isBookmarked ? 'Unsave Post' : 'Save Post'}>
              <IconButton disableRipple onClick={() => handleBookmarkClick()}>
                {isBookmarked ? (
                  <BookmarkIcon
                    sx={{
                      fontSize: {
                        xs: 20,
                        sm: 20,
                        md: 20,
                        lg: 20,
                        xl: 20,
                      },
                      mt: 0,
                      color: 'crimson',
                    }}
                  />
                ) : (
                  <BookmarkBorderIcon
                    sx={{
                      fontSize: {
                        xs: 20,
                        sm: 20,
                        md: 20,
                        lg: 20,
                        xl: 20,
                      },
                      mt: 0,
                      color: 'crimson',
                      '&:hover': {
                        transform: 'scale(1.11)', // Optional: adds a slight zoom effect
                        transition:
                          'transform 0.5s ease-in-out, box-shadow 0.3s ease-in-out',
                      },
                    }}
                  />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default DashboardCards;

// //to manage sharing post via social media etc..
// const handleShare = (platform) => {
//   const postUrl = `${window.location.origin}/posts/${postID}`;
//   let shareUrl = '';
//   const shareText = `${title}\n\n${summary}\n\n${postUrl}`; // Including title, summary, and URL

//   switch (platform) {
//     case 'twitter':
//       shareUrl = `https://twitter.com/share?url=${encodeURIComponent(
//         postUrl
//       )}&text=${encodeURIComponent(title)}`;
//       break;
//     case 'facebook':
//       shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
//         postUrl
//       )}&quote=${encodeURIComponent(shareText)}`;
//       break;
//     case 'email':
//       shareUrl = `mailto:?subject=${encodeURIComponent(
//         title
//       )}&body=${encodeURIComponent(shareText)}`;
//       break;
//       break;
//     case 'message':
//       shareUrl = `sms:?body=${encodeURIComponent(shareText)}`;

//       break;
//     case 'copy':
//       navigator.clipboard.writeText(postUrl);
//       alert('Link copied to clipboard!');
//       return;
//     default:
//       break;
//   }
//   if (shareUrl) {
//     window.open(shareUrl, '_blank', 'noopener,noreferrer');
//   }
// };

//Opens post in its own page -  not used anymore becos of detailed view
// const handleClick = () => {
//   navigate(`/post/${postID}`, {
//     state: {
//       images,
//       title,
//       summary,
//       photoURL,
//       username,
//       externalLinks,
//       certification,
//       name,
//       email,
//       phoneNumber,
//       address,
//       date,
//       category,
//       country,
//     },
//   });
// };

//What happens when I click the card, maybe include respective links in future?
// const handleCardClick = () => {
//   // window.location.href = link;
//   window.open(link, '_blank', 'noopener,noreferrer');
// };
